import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  ArrowLeft,
  Download,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Calendar,
  User,
  Mail,
  Briefcase,
  XCircle,
  Printer,
  CreditCard
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { openCheckout } from '../utils/checkout';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Send },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500 border-gray-300', icon: XCircle },
};

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const storedUserType = localStorage.getItem('userType');
    setUserType(storedUserType);
    fetchInvoice();
    
    // Check for payment status from URL params
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast({
        title: 'Payment Successful',
        description: 'Your payment has been processed. The invoice will be marked as paid shortly.',
      });
    } else if (paymentStatus === 'cancelled') {
      toast({
        title: 'Payment Cancelled',
        description: 'The payment was cancelled. You can try again.',
        variant: 'destructive',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [invoiceId, searchParams]);

  const fetchInvoice = async () => {
    try {
      const response = await invoiceAPI.getInvoice(invoiceId);
      setInvoice(response.data.invoice);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice',
        variant: 'destructive',
      });
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await invoiceAPI.downloadPDF(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoice.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'PDF Downloaded',
        description: `Invoice ${invoice.invoice_number} downloaded successfully`,
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: 'Download Failed',
        description: 'Could not download the invoice PDF',
        variant: 'destructive',
      });
    }
  };

  const handleSendInvoice = async () => {
    setActionLoading(true);
    try {
      await invoiceAPI.sendInvoice(invoiceId);
      toast({
        title: 'Invoice Sent',
        description: 'The invoice has been sent to the contractor',
      });
      fetchInvoice();
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send invoice',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try {
      await invoiceAPI.markPaid(invoiceId);
      toast({
        title: 'Invoice Marked as Paid',
        description: 'The invoice status has been updated',
      });
      fetchInvoice();
    } catch (error) {
      console.error('Error marking invoice paid:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update invoice',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePayInvoice = async () => {
    setPaymentLoading(true);
    try {
      const response = await invoiceAPI.createPaymentSession(invoiceId);
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        openCheckout(response.data.checkout_url);
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating payment session:', error);
      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Could not initiate payment. Please try again.',
        variant: 'destructive',
      });
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const StatusIcon = statusConfig[invoice.status]?.icon || FileText;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 print:bg-white print:py-0" data-testid="invoice-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 print:px-0">
        {/* Header - Hide on print */}
        <div className="flex items-center justify-between mb-8 print:hidden">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/invoices')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Invoice {invoice.invoice_number}
              </h1>
              <Badge className={`mt-1 ${statusConfig[invoice.status]?.color}`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig[invoice.status]?.label}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePrint}
              data-testid="print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              data-testid="download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            {userType === 'worker' && invoice.status === 'draft' && (
              <Button
                onClick={handleSendInvoice}
                disabled={actionLoading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="send-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Invoice
              </Button>
            )}
            {invoice.status === 'sent' && (
              <Button
                onClick={handleMarkPaid}
                disabled={actionLoading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="mark-paid-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Paid
              </Button>
            )}
            {/* Pay Invoice button for contractors */}
            {userType === 'contractor' && (invoice.status === 'sent' || invoice.status === 'overdue') && (
              <Button
                onClick={handlePayInvoice}
                disabled={paymentLoading}
                className="bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white font-bold"
                data-testid="pay-invoice-btn"
              >
                {paymentLoading ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay Invoice
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Invoice Document */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:shadow-none print:border-none">
          {/* Invoice Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-t-xl print:rounded-none">
            <div className="flex justify-between items-start">
              <div>
                {invoice.company_logo && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${invoice.company_logo}`}
                    alt="Company logo"
                    className="h-12 w-auto max-w-[160px] object-contain bg-white rounded-lg p-1 mb-3"
                    data-testid="invoice-company-logo"
                  />
                )}
                <h2 className="text-3xl font-bold">INVOICE</h2>
                <p className="text-blue-200 mt-1">{invoice.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-blue-200 text-sm">Date</p>
                <p className="font-semibold">
                  {new Date(invoice.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                {invoice.due_date && (
                  <>
                    <p className="text-blue-200 text-sm mt-2">Due Date</p>
                    <p className="font-semibold">
                      {new Date(invoice.due_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* From/To Section */}
          <div className="p-8 border-b border-gray-200 dark:border-gray-700">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">From</h3>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">{invoice.worker_name}</p>
                <p className="text-gray-600 dark:text-gray-400 flex items-center gap-2 mt-1">
                  <Mail className="h-4 w-4" />
                  {invoice.worker_email}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">Bill To</h3>
                <p className="font-semibold text-gray-900 dark:text-white text-lg">{invoice.contractor_name}</p>
                <p className="text-gray-600 dark:text-gray-400 flex items-center gap-2 mt-1">
                  <Mail className="h-4 w-4" />
                  {invoice.contractor_email}
                </p>
              </div>
            </div>

            {invoice.job_title && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Related Job
                </p>
                <p className="font-medium text-gray-900 dark:text-white mt-1">{invoice.job_title}</p>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="p-8">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 dark:border-gray-600">
                  <th className="text-left py-3 text-gray-600 dark:text-gray-400 font-semibold">Description</th>
                  <th className="text-right py-3 text-gray-600 dark:text-gray-400 font-semibold">Qty</th>
                  <th className="text-right py-3 text-gray-600 dark:text-gray-400 font-semibold">Unit Price</th>
                  <th className="text-right py-3 text-gray-600 dark:text-gray-400 font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items?.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-4 text-gray-900 dark:text-white">{item.description}</td>
                    <td className="py-4 text-right text-gray-600 dark:text-gray-400">{item.quantity}</td>
                    <td className="py-4 text-right text-gray-600 dark:text-gray-400">${item.unit_price?.toFixed(2)}</td>
                    <td className="py-4 text-right font-medium text-gray-900 dark:text-white">${item.amount?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="mt-8 flex justify-end">
              <div className="w-full md:w-72 space-y-2">
                <div className="flex justify-between py-2">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="font-medium text-gray-900 dark:text-white">${invoice.subtotal?.toFixed(2)}</span>
                </div>
                {invoice.tax_rate > 0 && (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600 dark:text-gray-400">Tax ({invoice.tax_rate}%)</span>
                    <span className="font-medium text-gray-900 dark:text-white">${invoice.tax_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between py-4 border-t-2 border-gray-900 dark:border-white">
                  <span className="text-xl font-bold text-gray-900 dark:text-white">Total</span>
                  <span className="text-xl font-bold text-blue-600">${invoice.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="px-8 pb-8">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Notes</h3>
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-8 bg-gray-50 dark:bg-gray-700/30 rounded-b-xl print:rounded-none border-t border-gray-200 dark:border-gray-700">
            <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
              <p>Thank you for your business!</p>
              <p className="mt-1">Generated by ConstructHelp</p>
            </div>
          </div>
        </div>

        {/* Payment Status Timeline - Hide on print */}
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 print:hidden">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Invoice Timeline</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Invoice Created</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(invoice.created_at).toLocaleString()}
                </p>
              </div>
            </div>
            {invoice.sent_at && (
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Send className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Invoice Sent</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(invoice.sent_at).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
            {invoice.paid_at && (
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Payment Received</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(invoice.paid_at).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;
